import type { Schema } from "../services/api";

type JsonRecord = Record<string, unknown>;

export function isWebAuthnSupported(): boolean {
  return typeof window !== "undefined" && "PublicKeyCredential" in window && typeof navigator.credentials?.create === "function";
}

export async function createPasskeyCredential(publicKey: unknown): Promise<NonNullable<Schema<"PasskeyRegisterRequest">["credential"]>> {
  if (!isWebAuthnSupported()) {
    throw new Error("PASSKEY_UNSUPPORTED");
  }
  const credential = (await runCredentialRequest(() =>
    navigator.credentials.create({
      publicKey: normalizeCreationOptions(publicKey)
    })
  )) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("PASSKEY_CANCELLED");
  }

  const response = credential.response as AuthenticatorAttestationResponse;
  return {
    id: credential.id,
    raw_id: arrayBufferToBase64Url(credential.rawId),
    type: "public-key",
    response: {
      client_data_json: arrayBufferToBase64Url(response.clientDataJSON),
      attestation_object: arrayBufferToBase64Url(response.attestationObject),
      transports: typeof response.getTransports === "function" ? response.getTransports() : undefined
    }
  };
}

export async function getPasskeyAssertion(publicKey: unknown): Promise<NonNullable<Schema<"PasskeyLoginFinishRequest">["credential"]>> {
  if (!isWebAuthnSupported() || typeof navigator.credentials?.get !== "function") {
    throw new Error("PASSKEY_UNSUPPORTED");
  }
  const credential = (await runCredentialRequest(() =>
    navigator.credentials.get({
      publicKey: normalizeRequestOptions(publicKey)
    })
  )) as PublicKeyCredential | null;

  if (!credential) {
    throw new Error("PASSKEY_CANCELLED");
  }

  const response = credential.response as AuthenticatorAssertionResponse;
  return {
    id: credential.id,
    raw_id: arrayBufferToBase64Url(credential.rawId),
    type: "public-key",
    response: {
      authenticator_data: arrayBufferToBase64Url(response.authenticatorData),
      client_data_json: arrayBufferToBase64Url(response.clientDataJSON),
      signature: arrayBufferToBase64Url(response.signature),
      user_handle: response.userHandle ? arrayBufferToBase64Url(response.userHandle) : undefined
    }
  };
}

function normalizeCreationOptions(value: unknown): PublicKeyCredentialCreationOptions {
  const source = asRecord(value);
  const user = asRecord(source.user);
  return {
    ...source,
    challenge: base64UrlToArrayBuffer(readString(source.challenge)),
    user: {
      ...user,
      id: base64UrlToArrayBuffer(readString(user.id))
    },
    excludeCredentials: normalizeCredentialDescriptors(source.excludeCredentials)
  } as PublicKeyCredentialCreationOptions;
}

function normalizeRequestOptions(value: unknown): PublicKeyCredentialRequestOptions {
  const source = asRecord(value);
  return {
    ...source,
    challenge: base64UrlToArrayBuffer(readString(source.challenge)),
    allowCredentials: normalizeCredentialDescriptors(source.allowCredentials)
  };
}

function normalizeCredentialDescriptors(value: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.map((item) => {
    const credential = asRecord(item);
    return {
      ...credential,
      id: base64UrlToArrayBuffer(readString(credential.id))
    } as PublicKeyCredentialDescriptor;
  });
}

function asRecord(value: unknown): JsonRecord {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as JsonRecord) : {};
}

function readString(value: unknown): string {
  if (typeof value !== "string" || !value) {
    throw new Error("PASSKEY_OPTIONS_INVALID");
  }
  return value;
}

function base64UrlToArrayBuffer(value: string): ArrayBuffer {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

async function runCredentialRequest(action: () => Promise<Credential | null>): Promise<Credential | null> {
  try {
    return await action();
  } catch (error) {
    if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "AbortError")) {
      throw new Error("PASSKEY_CANCELLED");
    }
    throw error;
  }
}
